(() => {
  if (typeof customElements === 'undefined' || customElements.get('sasha-business-analytics')) return;

  class SashaBusinessAnalytics extends HTMLElement {
    connectedCallback() {
      if (this.controller || String(window.Shopify?.theme?.id) !== this.dataset.themeId) return;
      try {
        this.controller = new AbortController();
        this.preview = window.Shopify.theme.role !== 'main' || window.Shopify.designMode === true;
        this.records = new Map();
        this.seen = new Set();
        this.commissions = new WeakMap();
        this.events = [];
        this.eventCount = 0;
        this.leadKey = `sasha-ga4-lead:${this.dataset.themeId}`;
        if (this.preview) {
          this.diagnostics = document.createElement('script');
          this.diagnostics.type = 'application/json';
          this.diagnostics.dataset.sashaAnalyticsDiagnostics = '';
          this.append(this.diagnostics);
        }
        const listen = (node, type, handler, capture = false) => node.addEventListener(type, this.safe(handler), { signal: this.controller.signal, capture });
        if (typeof IntersectionObserver === 'function') {
          this.observer = new IntersectionObserver(this.safe((entries) => {
            for (const entry of entries) {
              const record = this.records.get(entry.target);
              if (!record) continue;
              record.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
              this.schedule(record);
            }
          }), { threshold: [0, 0.5] });
        }
        listen(document, 'sasha:analytics-catalog', () => this.syncCatalogs());
        listen(document, 'shopify:section:load', () => { this.syncCatalogs(); this.readiness(); });
        listen(document, 'shopify:section:unload', () => this.syncCatalogs());
        listen(document, 'visibilitychange', () => this.refreshVisibility());
        listen(document, 'close', () => this.refreshVisibility(), true);
        listen(document, 'load', (event) => {
          const image = event.target.closest?.('[data-analytics-image]');
          this.schedule(this.records.get(image));
        }, true);
        listen(document, 'click', (event) => this.onClick(event));
        listen(document, 'auxclick', (event) => this.onClick(event));
        listen(document, 'input', (event) => this.onInput(event));
        listen(document, 'change', (event) => this.onInput(event));
        listen(document, 'submit', (event) => this.onSubmit(event), true);
        listen(document, 'sasha:analytics-photos', (event) => this.onPhotos(event));
        listen(document, 'shopify:cart:error', (event) => this.onCartError(event));
        listen(document, 'visitorConsentCollected', () => {
          if (!this.analyticsAllowed()) {
            try { sessionStorage.removeItem(this.leadKey); } catch {}
          }
          this.readiness();
          this.retryReadiness();
        });
        listen(window, 'load', () => this.readiness());
        listen(window, 'pagehide', () => {
          for (const record of this.records.values()) this.cancel(record);
          clearTimeout(this.readinessTimer);
        });
        listen(window, 'pageshow', () => { this.syncCatalogs(); this.readiness(); });
        this.syncCatalogs();
        this.readiness();
        if (!window.Shopify.customerPrivacy && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            if (!error && this.controller) this.safe(() => this.readiness())();
          });
        }
        this.retryReadiness();
      } catch {
        this.disconnectedCallback();
      }
    }

    safe(handler) {
      return (...args) => { try { handler(...args); } catch {} };
    }

    analyticsAllowed() {
      try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; } catch { return false; }
    }

    ready() {
      return this.analyticsAllowed() && typeof window.gtag === 'function';
    }

    send(name, parameters) {
      try {
        if (!this.ready()) return false;
        const payload = { send_to: this.dataset.measurementId, ...parameters };
        if (this.preview) {
          this.eventCount++;
          this.events.push({ event: name, parameters: payload });
          this.events = this.events.slice(-50);
          this.updateDiagnostics();
        } else {
          window.gtag('event', name, payload);
        }
        return true;
      } catch { return false; }
    }

    updateDiagnostics() {
      if (!this.diagnostics) return;
      this.diagnostics.textContent = JSON.stringify({
        version: 'business-v1', mode: 'preview', themeId: this.dataset.themeId,
        analyticsAllowed: this.analyticsAllowed(), tagReady: typeof window.gtag === 'function',
        eventCount: this.eventCount, events: this.events,
      });
    }

    retryReadiness() {
      clearTimeout(this.readinessTimer);
      this.readinessTimer = setTimeout(this.safe(() => { if (this.controller) this.readiness(); }), 1000);
    }

    readiness() {
      this.refreshVisibility();
      this.confirmLead();
      this.updateDiagnostics();
    }

    productId(value) {
      return /^\d+$/.test(value || '') ? `p_${value}` : '';
    }

    listName(root) {
      return `${root.dataset.analyticsList}:${root.dataset.analyticsFilter || 'all'}`;
    }

    cardData(card, root) {
      const productId = this.productId(card.dataset.analyticsProductId);
      if (!productId || !/^\d+$/.test(card.dataset.analyticsVariantId || '')) return null;
      const cards = [...root.querySelectorAll('[data-analytics-product-id]')].filter((item) => !item.hidden);
      return {
        product_id: productId,
        artwork_handle: card.dataset.analyticsHandle,
        list_name: this.listName(root),
        list_position: cards.indexOf(card) + 1,
      };
    }

    syncCatalogs() {
      if (!this.observer) return;
      for (const [image, record] of this.records) {
        if (!image.isConnected || !record.root.isConnected || record.card.hidden || record.context !== this.listName(record.root)) {
          this.cancel(record);
          this.observer.unobserve(image);
          this.records.delete(image);
        }
      }
      for (const root of document.querySelectorAll('[data-analytics-list]')) {
        for (const card of root.querySelectorAll('[data-analytics-product-id]')) {
          const image = card.querySelector('[data-analytics-image]');
          if (!image || !image.querySelector('img') || card.hidden || !this.cardData(card, root) || this.records.has(image)) continue;
          const record = { image, card, root, context: this.listName(root), visible: false, timer: null };
          this.records.set(image, record);
          this.observer.observe(image);
        }
      }
    }

    cancel(record) {
      if (!record) return;
      clearTimeout(record.timer);
      record.timer = null;
    }

    refreshVisibility() {
      for (const record of this.records.values()) this.schedule(record);
    }

    schedule(record) {
      if (!record) return;
      if (!record.visible || document.visibilityState !== 'visible' || document.querySelector('dialog[open]') || !this.ready()) {
        this.cancel(record);
        return;
      }
      const key = `${record.context}:${record.card.dataset.analyticsProductId}`;
      if (record.timer !== null || this.seen.has(key)) return;
      record.timer = setTimeout(this.safe(() => {
        record.timer = null;
        const image = record.image.querySelector('img');
        if (!record.visible || !record.image.isConnected || record.card.hidden || document.visibilityState !== 'visible' || document.querySelector('dialog[open]') || !record.image.getClientRects().length || !image?.complete || !image.naturalWidth || this.seen.has(key)) return;
        const data = this.cardData(record.card, record.root);
        if (data && this.send('catalog_item_view', data)) this.seen.add(key);
      }), 500);
    }

    onClick(event) {
      if (!event.isTrusted || event.defaultPrevented || ![0, 1].includes(event.button)) return;
      const target = event.target;
      const form = target.closest?.('.sasha-portrait-product__form');
      if (form && target.closest('[data-photo-trigger], [data-photo-source]')) this.startCommission(form.closest('.sasha-portrait-product'));
      const card = target.closest?.('[data-analytics-product-id]');
      const root = card?.closest('[data-analytics-list]');
      if (!root || card.hidden) return;
      const quick = target.closest('[data-sasha-quick-add-open]');
      const link = target.closest('a[href]');
      if (quick) {
        if (event.type !== 'click' || event.button !== 0 || !document.getElementById(quick.dataset.sashaQuickAddOpen)?.open) return;
      } else if (!link || link.closest('dialog') || !link.getAttribute('href')?.includes('/products/')) return;
      const data = this.cardData(card, root);
      if (!data) return;
      const price = Number(card.dataset.analyticsPrice);
      const item = {
        item_id: `${this.dataset.itemPrefix}${card.dataset.analyticsProductId}_${card.dataset.analyticsVariantId}`,
        item_name: card.dataset.analyticsTitle,
        item_variant: card.dataset.analyticsVariant,
        item_list_id: data.list_name, item_list_name: data.list_name, index: data.list_position - 1,
      };
      if (Number.isFinite(price) && price >= 0) item.price = price;
      this.send('select_item', { ...data, selection_action: quick ? 'quick_add' : 'product_page',
        item_list_id: data.list_name, item_list_name: data.list_name,
        currency: root.dataset.analyticsCurrency, items: [item] });
    }

    commissionState(root) {
      if (!this.commissions.has(root)) this.commissions.set(root, { started: false, photos: false });
      return this.commissions.get(root);
    }

    commissionData(root) {
      const productId = this.productId(root?.dataset.productId);
      if (!productId) return null;
      const variantId = root.querySelector('[data-portrait-variant-id]')?.value;
      return {
        product_id: productId,
        ...(/^\d+$/.test(variantId || '') ? { variant_id: `v_${variantId}` } : {}),
        photo_count: Math.min(3, [...root.querySelectorAll('[data-photo-property]')].filter((input) => input.files?.length).length),
        has_notes: root.querySelector('textarea[name="properties[Notes]"]')?.value.trim() ? 'yes' : 'no',
      };
    }

    startCommission(root) {
      const data = this.commissionData(root);
      if (!data) return;
      const state = this.commissionState(root);
      if (!state.started && this.send('commission_start', { product_id: data.product_id })) state.started = true;
    }

    onInput(event) {
      if (!event.isTrusted) return;
      const root = event.target.closest?.('.sasha-portrait-product');
      if (root && event.target.closest('.sasha-portrait-product__form')) this.startCommission(root);
    }

    onPhotos(event) {
      const root = event.target.closest?.('.sasha-portrait-product');
      const data = this.commissionData(root);
      if (!data) return;
      this.startCommission(root);
      const state = this.commissionState(root);
      if (event.detail?.added > 0 && data.photo_count > 0 && !state.photos && this.send('commission_photos_selected', { product_id: data.product_id, photo_count: data.photo_count })) state.photos = true;
      for (const code of new Set(event.detail?.errors || [])) {
        if (['photo_type', 'photo_total_size', 'photo_count'].includes(code)) this.send('commerce_error', {
          product_id: data.product_id, error_source: 'commission_photos', error_code: code,
        });
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (!event.isTrusted || event.defaultPrevented || !(form instanceof HTMLFormElement) || form.matches(':invalid')) return;
      if (form.matches('.sasha-portrait-product__form')) {
        const root = form.closest('.sasha-portrait-product');
        this.startCommission(root);
        const data = this.commissionData(root);
        if (data) this.send('commission_submit', data);
      }
      const type = form.dataset.analyticsLeadForm;
      if (!['newsletter', 'contact'].includes(type) || !this.ready()) return;
      try { sessionStorage.setItem(this.leadKey, JSON.stringify({ type, path: location.pathname, at: Date.now() })); } catch {}
    }

    confirmLead() {
      if (!this.ready()) return;
      try {
        const pending = JSON.parse(sessionStorage.getItem(this.leadKey) || 'null');
        if (!pending) return;
        if (!Number.isFinite(pending.at) || Date.now() - pending.at > 600000 || Date.now() < pending.at || pending.path !== location.pathname) {
          sessionStorage.removeItem(this.leadKey);
          return;
        }
        if (!['newsletter', 'contact'].includes(pending.type)) return;
        const success = document.querySelector(`[data-analytics-lead-success="${pending.type}"]`);
        if (!success) return;
        sessionStorage.removeItem(this.leadKey);
        this.send('generate_lead', { lead_type: pending.type });
      } catch {}
    }

    onCartError(event) {
      const form = event.target.closest?.('product-form-component');
      const productId = this.productId(form?.dataset.productId);
      if (!productId || !['INVALID', 'SERVICE_UNAVAILABLE'].includes(event.code)) return;
      this.send('commerce_error', { product_id: productId, error_source: 'product_cart',
        error_code: event.code === 'INVALID' ? 'cart_rejected' : 'cart_network' });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      if (this.records) for (const record of this.records.values()) this.cancel(record);
      this.records?.clear();
      clearTimeout(this.readinessTimer);
      this.diagnostics?.remove();
    }
  }

  customElements.define('sasha-business-analytics', SashaBusinessAnalytics);
})();
